/** Names the client may POST to `/analytics/batch`. */
export const CLIENT_ANALYTICS_EVENTS = [
  'welcome_pack_opened',
  'first_deck_saved',
  'first_duel_started',
  'duel_abandoned',
  'engine_error',
  'duel_stall',
  'card_fault',
  'summon_lock',
  'board_gap',
  'board_blank',
  'duel_hidden',
  'queue_wait',
  'mat_equipped',
  'sleeve_equipped',
  'pack_buy_refused',
  'coin_open',
  'coin_close',
] as const

/** Includes server-only names such as `admin_login`. */
export const ANALYTICS_EVENTS = [
  ...CLIENT_ANALYTICS_EVENTS,
  'admin_login',
  'action_rejected',
  'duel_link',
  'pack_buy',
  'tournament_entry',
] as const

export type ClientAnalyticsEvent = (typeof CLIENT_ANALYTICS_EVENTS)[number]
export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[number]

export type AnalyticsPropValue = string | number | boolean

export type AnalyticsEventPayload = {
  event: AnalyticsEventName
  props: Record<string, AnalyticsPropValue>
}

const CLIENT_SET = new Set<string>(CLIENT_ANALYTICS_EVENTS)
const ALL_SET = new Set<string>(ANALYTICS_EVENTS)

const BATCH_MAX = 20
const PROP_KEYS_MAX = 16
const PROP_STRING_MAX = 180

/**
 * True when the client is allowed to emit this event name.
 */
export function isClientAnalyticsEvent(
  value: unknown,
): value is ClientAnalyticsEvent {
  return typeof value === 'string' && CLIENT_SET.has(value)
}

/**
 * True when the name is in the full catalog (client + server).
 */
export function isAnalyticsEventName(
  value: unknown,
): value is AnalyticsEventName {
  return typeof value === 'string' && ALL_SET.has(value)
}

function asPropValue(raw: unknown): AnalyticsPropValue | null {
  if (typeof raw === 'boolean') return raw
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw > 1e12) return 1e12
    if (raw < -1e12) return -1e12
    return raw
  }
  if (typeof raw === 'string') return raw.slice(0, PROP_STRING_MAX)
  return null
}

/**
 * Drops unknown keys and non-scalar values. Empty object when nothing usable.
 */
export function sanitizeAnalyticsProps(
  raw: unknown,
): Record<string, AnalyticsPropValue> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, AnalyticsPropValue> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Object.keys(out).length >= PROP_KEYS_MAX) break
    const id = key.trim().slice(0, 32)
    if (!id) continue
    const parsed = asPropValue(value)
    if (parsed === null) continue
    out[id] = parsed
  }
  return out
}

/**
 * Coerce a vs-client batch body. Unknown event names are dropped.
 */
export function parseAnalyticsBatch(raw: unknown): AnalyticsEventPayload[] {
  if (!raw || typeof raw !== 'object') return []
  const list = (raw as { events?: unknown }).events
  if (!Array.isArray(list)) return []
  const out: AnalyticsEventPayload[] = []
  for (const item of list) {
    if (out.length >= BATCH_MAX) break
    if (!item || typeof item !== 'object') continue
    const rec = item as { event?: unknown; props?: unknown }
    if (!isClientAnalyticsEvent(rec.event)) continue
    out.push({
      event: rec.event,
      props: sanitizeAnalyticsProps(rec.props),
    })
  }
  return out
}

const DURATION_MAX_MS = 8 * 60 * 60 * 1000

/**
 * Clamp a client-reported duration. Null when missing or not a number.
 */
export function parseDurationMs(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null
  const n = Math.trunc(raw)
  if (n < 0) return 0
  return n > DURATION_MAX_MS ? DURATION_MAX_MS : n
}
